import { useEffect, useRef, useState, type ReactNode } from "react";
import { HeroInlineLogo } from "@/components/dataTable";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  ABSENCE_STATUS_LABELS,
  absenceMarkerClassName,
  formatAbsenceMarkerLabel,
  getAbsenceTypeLabel,
  type AbsenceDayMarker,
} from "@/lib/employeeAbsences";
import { calendarDayTitle } from "@/lib/employeeCalendar";
import {
  formatHours,
  listEmployeeTimes,
  type EmployeeTimeEntry,
} from "@/lib/employeeTimes";
import { formatEntryProjectLabel } from "@/pages/admin/zeiterfassung/columns";
import { EmployeeTimeTimesheetBadge } from "@/pages/admin/zeiterfassung/EmployeeTimeTimesheetBadge";
import {
  formatDayHeading,
  formatOvertimeBalanceHhmm,
  todayIso,
  type YearGridCell,
} from "@/pages/admin/zeiterfassung/yearOverviewUtils";

interface YearDayDetailDialogProps {
  cell: YearGridCell | null;
  employeeLogin: string;
  employeeDisplay: string;
  onClose: () => void;
}

function formatTimeRange(entry: EmployeeTimeEntry): string | null {
  if (!entry.time_from || !entry.time_to) return null;
  return `${entry.time_from.slice(0, 5)} – ${entry.time_to.slice(0, 5)} Uhr`;
}

function EntryRow({ entry }: { entry: EmployeeTimeEntry }) {
  const isBreak = entry.entry_type === "break";
  const timeRange = formatTimeRange(entry);
  const projectLabel = entry.auftragsnummer
    ? [
        formatEntryProjectLabel(entry),
        entry.beschreibung,
        entry.kunde,
        entry.ort_zusatz || entry.ort,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <div className="rounded-ios border border-separator bg-fill-tertiary/25 px-3 py-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 flex flex-wrap items-center gap-1.5 text-sm font-semibold text-label">
          <span className="truncate">{isBreak ? "Pause" : projectLabel ?? "Ohne Projekt"}</span>
          {!isBreak ? <EmployeeTimeTimesheetBadge entry={entry} /> : null}
          {!isBreak && entry.is_homeoffice ? (
            <span className="inline-flex shrink-0 rounded-md bg-accent/10 px-1.5 py-0.5 text-[11px] font-semibold text-accent">
              Homeoffice
            </span>
          ) : null}
        </span>
        <span
          className={`shrink-0 text-sm font-bold tabular-nums ${isBreak ? "text-label-secondary" : "text-accent"}`}
        >
          {formatHours(entry.hours_decimal)}
        </span>
      </div>
      {(timeRange || entry.beschreibung) && (
        <div className="mt-0.5 truncate text-xs text-label-secondary">
          {[timeRange, entry.beschreibung].filter(Boolean).join(" · ")}
        </div>
      )}
      {entry.comment && (
        <p className="mt-1 text-xs italic text-label-secondary">{entry.comment}</p>
      )}
    </div>
  );
}

function AbsenceRow({ marker }: { marker: AbsenceDayMarker }) {
  const fractionHint =
    marker.day_fraction === 0.5
      ? marker.half_day_part === "am"
        ? " (vormittags)"
        : " (nachmittags)"
      : "";
  return (
    <div className="flex items-center gap-2 rounded-ios border border-separator bg-fill-tertiary/25 px-3 py-2.5">
      <span
        className={`inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[11px] font-bold ${absenceMarkerClassName(marker)}`}
      >
        {formatAbsenceMarkerLabel(marker)}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm text-label">
        {getAbsenceTypeLabel(marker.absence_type)}
        {fractionHint}
      </span>
      <span
        className={`shrink-0 text-xs font-medium ${marker.status === "pending" ? "text-amber-600 dark:text-amber-400" : "text-label-secondary"}`}
      >
        {ABSENCE_STATUS_LABELS[marker.status]}
      </span>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-[11px] font-bold uppercase tracking-wide text-label-secondary">
      {children}
    </h3>
  );
}

export function YearDayDetailDialog({
  cell,
  employeeLogin,
  employeeDisplay,
  onClose,
}: YearDayDetailDialogProps) {
  // Letzte Zelle merken, damit der Inhalt während der Schließ-Animation erhalten bleibt.
  const lastCellRef = useRef<YearGridCell | null>(null);
  if (cell) lastCellRef.current = cell;
  const shown = cell ?? lastCellRef.current;

  const [entries, setEntries] = useState<EmployeeTimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!cell) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setEntries([]);
    listEmployeeTimes({
      user_login: employeeLogin,
      work_date_from: cell.iso,
      work_date_to: cell.iso,
    })
      .then((rows) => {
        if (alive) setEntries(rows);
      })
      .catch((err) => {
        if (alive) {
          setError(
            err instanceof ApiError ? err.message : "Tagesdetails konnten nicht geladen werden.",
          );
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [cell, employeeLogin]);

  if (!shown) return null;

  const workEntries = entries.filter((e) => e.entry_type !== "break");
  const breakEntries = entries.filter((e) => e.entry_type === "break");
  const workHours = workEntries.reduce((sum, e) => sum + Number(e.hours_decimal || 0), 0);
  const breakHours = breakEntries.reduce((sum, e) => sum + Number(e.hours_decimal || 0), 0);
  const breakMinutes = Math.round(breakHours * 60);

  const hasAnything =
    entries.length > 0 ||
    shown.markers.length > 0 ||
    shown.heroMarkers.length > 0 ||
    shown.heroHours > 0 ||
    shown.payoutHours > 0 ||
    Boolean(shown.calendarDay);

  return (
    <Dialog
      open={cell !== null}
      onClose={onClose}
      size="md"
      title={formatDayHeading(shown.iso)}
      description={employeeDisplay || employeeLogin}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-ios border border-separator bg-fill-tertiary/25 px-3 py-2.5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-label-secondary">
              Arbeitszeit
            </div>
            <div className="mt-0.5 text-lg font-bold tabular-nums text-blue-600 dark:text-blue-300">
              {formatHours(workHours)}
            </div>
          </div>
          <div className="rounded-ios border border-separator bg-fill-tertiary/25 px-3 py-2.5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-label-secondary">
              Pause
            </div>
            <div className="mt-0.5 text-lg font-bold tabular-nums text-label">
              {breakMinutes > 0 ? `${breakMinutes} min` : "—"}
            </div>
          </div>
          {shown.overtimeBalanceHours != null && (
            <div className="col-span-2 rounded-ios border border-separator bg-fill-tertiary/25 px-3 py-2.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-label-secondary">
                {shown.iso > todayIso() ? "Prognose Aktuell +/-" : "Aktuell +/-"}
              </div>
              <div
                className={`mt-0.5 text-lg font-bold tabular-nums ${
                  shown.overtimeBalanceHours < -0.0001
                    ? "text-red-600 dark:text-red-300"
                    : "text-green-700 dark:text-green-300"
                }`}
              >
                {formatOvertimeBalanceHhmm(shown.overtimeBalanceHours)}
              </div>
              {shown.iso > todayIso() && (
                <div className="mt-0.5 text-[11px] text-label-tertiary">
                  Fortgeschrieben ohne künftige Ist-Zeiten (nur Soll/Abwesenheit)
                </div>
              )}
            </div>
          )}
          {shown.payoutHours > 0 && (
            <div className="col-span-2 rounded-ios border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-200">
                Ausbezahlte Überstunden
              </div>
              <div className="mt-0.5 text-lg font-bold tabular-nums text-emerald-900 dark:text-emerald-100">
                {formatHours(shown.payoutHours)}
              </div>
            </div>
          )}
        </div>

        {shown.calendarDay && (
          <div
            className={`flex items-center gap-2 rounded-ios px-3 py-2.5 text-sm font-medium ${
              shown.calendarDay.kind === "holiday"
                ? "bg-amber-500/15 text-amber-900 dark:text-amber-200"
                : shown.calendarDay.kind === "betriebsferien"
                  ? "bg-sky-500/10 text-sky-900 dark:text-sky-200"
                  : "bg-indigo-500/10 text-indigo-900 dark:text-indigo-200"
            }`}
          >
            <span className="font-bold">
              {shown.calendarDay.kind === "holiday"
                ? "Feiertag"
                : shown.calendarDay.kind === "betriebsferien"
                  ? "Betriebsferien"
                  : "Brückentag"}
            </span>
            <span className="min-w-0 flex-1 truncate">
              {calendarDayTitle(shown.calendarDay)}
            </span>
          </div>
        )}

        {shown.markers.length > 0 && (
          <div className="space-y-1.5">
            <SectionTitle>Abwesenheit (App)</SectionTitle>
            {shown.markers.map((marker, idx) => (
              <AbsenceRow key={`${marker.absence_type}-${idx}`} marker={marker} />
            ))}
          </div>
        )}

        {(shown.heroHours > 0 || shown.heroMarkers.length > 0) && (
          <div className="space-y-1.5">
            <SectionTitle>Hero</SectionTitle>
            {shown.heroHours > 0 && (
              <div className="rounded-ios border border-violet-500/30 bg-violet-500/10 px-3 py-2.5">
                <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-violet-800 dark:text-violet-200">
                  <HeroInlineLogo className="h-[1em]" />
                  Erfasste Zeit in Hero
                </div>
                <div className="mt-0.5 text-lg font-bold tabular-nums text-violet-900 dark:text-violet-100">
                  {formatHours(shown.heroHours)}
                </div>
              </div>
            )}
            {shown.heroMarkers.map((marker, idx) => (
              <div
                key={`hero-${marker.absence_type}-${idx}`}
                className="flex items-center gap-2 rounded-ios border border-violet-500/25 bg-violet-500/5 px-3 py-2.5"
              >
                <span
                  className={`inline-flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-bold ${absenceMarkerClassName(marker)}`}
                >
                  <HeroInlineLogo />
                  {formatAbsenceMarkerLabel(marker)}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-label">
                  {getAbsenceTypeLabel(marker.absence_type)}
                </span>
              </div>
            ))}
          </div>
        )}

        {error && (
          <Alert variant="error" title="Fehler">
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : entries.length > 0 ? (
          <div className="space-y-1.5">
            <SectionTitle>
              {entries.length === 1 ? "1 Eintrag" : `${entries.length} Einträge`}
            </SectionTitle>
            {entries.map((entry) => (
              <EntryRow key={entry.id} entry={entry} />
            ))}
          </div>
        ) : (
          !error &&
          !hasAnything && (
            <div className="rounded-ios border border-dashed border-separator px-3 py-6 text-center text-sm text-label-secondary">
              Keine Einträge an diesem Tag.
            </div>
          )
        )}
      </div>
    </Dialog>
  );
}
